import React, { useEffect } from "react";
import { Modal, Form, Input, Button, Select, Checkbox, Table } from "antd";
import { ISubAdminPayload } from "../../../types/api/subAdmin";
import { AdminPermissionModule, IUserPermission } from "../../../types/api/user";
import { useSubAdminCreate, useSubAdminUpdate } from "../../../hooks/api/subAdmin/hooks";
import { useMessageApi } from "../../../provider/AntdNotification";

const MODULES: { key: AdminPermissionModule; label: string }[] = [
  { key: "DASHBOARD", label: "Dashboard" },
  { key: "USERS", label: "Users" },
  { key: "ORDERS", label: "Orders" },
  { key: "ENQUIRIES", label: "Enquiries" },
  { key: "NOTIFICATIONS", label: "Notifications" },
  { key: "CATEGORY", label: "Category" },
  { key: "MASTERS", label: "Masters" },
  { key: "ARTISTS", label: "Artists" },
  { key: "PRODUCTS", label: "Sculptures (incl. variants)" },
  { key: "LOCATIONS", label: "Locations" },
  { key: "REVIEWS", label: "Reviews" },
  { key: "FAQS", label: "FAQs" },
  { key: "CMS", label: "CMS (all pages)" },
];

const ACTIONS: { key: IUserPermission["action"]; label: string }[] = [
  { key: "VIEW", label: "View" },
  { key: "CREATE", label: "Create" },
  { key: "EDIT", label: "Edit" },
  { key: "DELETE", label: "Delete" },
];

interface SubAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItem?: ISubAdminPayload | null;
}

const SubAdminModal: React.FC<SubAdminModalProps> = ({
  isOpen,
  onClose,
  selectedItem,
}) => {
  const messageApi = useMessageApi();
  const [form] = Form.useForm();
  const isEdit = !!selectedItem?._id;

  const createMutation = useSubAdminCreate({
    onSuccess: () => {
      messageApi.success("Admin user created successfully");
      onClose();
    },
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to create admin user"),
  });

  const updateMutation = useSubAdminUpdate({
    onSuccess: () => {
      messageApi.success("Admin user updated successfully");
      onClose();
    },
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to update admin user"),
  });

  useEffect(() => {
    if (!isOpen) return;
    if (selectedItem) {
      const permissionMap: Record<string, string[]> = {};
      (selectedItem.permissions || []).forEach((p) => {
        permissionMap[p.module] = [...(permissionMap[p.module] || []), p.action];
      });
      form.setFieldsValue({
        name: selectedItem.name,
        email: selectedItem.email,
        phone: selectedItem.phone,
        user_type: selectedItem.user_type || "SUB_ADMIN",
        permissionMap,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ user_type: "SUB_ADMIN", permissionMap: {} });
    }
  }, [isOpen, selectedItem, form]);

  const handleSubmit = (values: any) => {
    const permissions: IUserPermission[] = [];
    Object.entries(values.permissionMap || {}).forEach(([module, actions]) => {
      (actions as string[] || []).forEach((action) => {
        permissions.push({ module: module as AdminPermissionModule, action: action as any });
      });
    });

    const payload: Partial<ISubAdminPayload> = {
      name: values.name,
      email: values.email,
      phone: values.phone,
      user_type: values.user_type,
      permissions,
    };
    if (values.password) payload.password = values.password;

    if (isEdit) {
      updateMutation.mutate({ id: selectedItem!._id!, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSuperAdmin = Form.useWatch("user_type", form) === "SUPER_ADMIN";

  return (
    <Modal
      title={isEdit ? "Edit Admin User" : "Add Admin User"}
      open={isOpen}
      onCancel={onClose}
      width={720}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={createMutation.isPending || updateMutation.isPending}
          onClick={() => form.submit()}
        >
          {isEdit ? "Save Changes" : "Create Admin User"}
        </Button>,
      ]}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} className="mt-4">
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input placeholder="Full name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Must be a valid email" },
            ]}
          >
            <Input placeholder="email@artparks.co.uk" disabled={isEdit} />
          </Form.Item>

          <Form.Item name="phone" label="Phone">
            <Input placeholder="Phone number" />
          </Form.Item>

          <Form.Item
            name="user_type"
            label="Role"
            rules={[{ required: true, message: "Role is required" }]}
          >
            <Select
              options={[
                { label: "Sub Admin (permissions below apply)", value: "SUB_ADMIN" },
                { label: "Super Admin (full access, permissions ignored)", value: "SUPER_ADMIN" },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="password"
            label={isEdit ? "Reset Password (leave blank to keep current)" : "Password"}
            rules={isEdit ? [] : [{ required: true, message: "Password is required" }, { min: 6, message: "At least 6 characters" }]}
          >
            <Input.Password placeholder={isEdit ? "Leave blank to keep current password" : "Set a password"} />
          </Form.Item>
        </div>

        {!isSuperAdmin && (
          <Form.Item label="Permissions" name="permissionMap" className="mt-2">
            <PermissionGrid />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

// Antd Form.Item wires value/onChange into this via context when used as a
// direct child - implemented as a controlled-ish grid instead for clarity.
const PermissionGrid: React.FC<{
  value?: Record<string, string[]>;
  onChange?: (value: Record<string, string[]>) => void;
}> = ({ value = {}, onChange }) => {
  const toggle = (module: string, action: string, checked: boolean) => {
    const current = new Set(value[module] || []);
    if (checked) current.add(action);
    else current.delete(action);
    onChange?.({ ...value, [module]: Array.from(current) });
  };

  return (
    <Table
      size="small"
      pagination={false}
      dataSource={MODULES}
      rowKey="key"
      columns={[
        { title: "Module", dataIndex: "label", key: "label", width: 200 },
        ...ACTIONS.map((a) => ({
          title: a.label,
          key: a.key,
          width: 80,
          align: "center" as const,
          render: (_: any, record: { key: string }) => (
            <Checkbox
              checked={(value[record.key] || []).includes(a.key)}
              onChange={(e) => toggle(record.key, a.key, e.target.checked)}
            />
          ),
        })),
      ]}
    />
  );
};

export default SubAdminModal;
